import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import {
  FlaskConical, Wrench, FileText, Layers, Calendar, Stethoscope, ArrowUp, ArrowDown,
  Coins, Lock, PackageSearch, X,
} from 'lucide-react'
import { postJson } from '../lib/api.js'
import { useMe } from '../lib/me.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { isLabOnly, canManage, labStageScope } from '../lib/role.js'
import { usePersistedState } from '../lib/persist.js'
import { nameInitial } from '../lib/text.js'
import StagePicker from '../components/lab/StagePicker.jsx'
import LabDetailDrawer from '../components/lab/LabDetailDrawer.jsx'
import MakerPicker from '../components/lab/MakerPicker.jsx'
import {
  LAB_YEARS, fetchLabForSummary, isPriceLockedRow, stageShort,
  labVendorName, labDoctorCode, labDoctorImg,
  labPatientName, labPatientHN, labPrice, labPriceClinic, labPriceDoctor, hasRetainer,
  isStageLockedRow, isPendingDeductStage, selectableStages,
  isLabOnlyVendor, STAGE_LAB_DONE_ID, stagesForScope, shownStage, stageKeyFor,
  fetchLabByDate, bkkMonthWindow, labChaseState, labReceived,
} from '../lib/lab.js'
import useLabRefs from '../lib/useLabRefs.js'
import { postLabEdit, applyStagePatch, applyEditPatch } from '../lib/labWrites.js'
import {
  Page, PageHeader, Tabs, Segmented, FilterCard, useFilters, Field, Select, SearchSelect, SearchInput,
  BranchSelect, branchFilterIds, DataTable, useTableState, Badge, Banner, Button, Checkbox,
  ConfirmDialog, useToast, PatientName, fmtDate, fmtMoney, fmtNumber, bkkTodayISO, TH_MONTHS_LONG,
} from '../kit'

// LAB review page — laid out like รายการตามเบิก (Reimbursement): a Filters card, a
// grouped Summary card, and a Ledger card with client pagination.
//   • The stage filter is a dropdown (was chips).
//   • There is NO standalone Lab filter — instead the Summary groups labs (In-house /
//     external) and clicking a lab filters the ledger (the "select"), the same way the
//     payer summary drives the reimbursement ledger.
//
// Data: fetchLabForSummary loads the filtered set once (year/month/branch/doctor/search
// are server params; stage + lab-selection are applied client-side). A Lab-only account
// (role.js#isLabOnly) is scoped to the 4 lab-stage statuses and sees no cost figures.

const INDIGO = '#6366f1'
const PER_PAGE_OPTS = [50, 100, 200]
// ── THE TWO TABS, AND WHY THE PAGE NEEDS BOTH (Ram, 2026-09-13) ──────────────
// Same filters, same ledger, same drawer — the ONLY difference is which column the
// ปี/เดือน filter matches, and that difference is the whole feature:
//
//   หัก Lab  → `lab.year` / `lab.month` (Xano GET /lab, api 193). PAYROLL columns.
//              df_doctor/gen2 rewrites them to move an unsettled in-house lab into the
//              month it is generating. They answer "which month is this charged to",
//              which is the right question for the หักยอดหมอ action at the bottom.
//   ตาม Lab  → `lab.date` (Xano api 652, claude group). The handover day, which follows
//              the linked appointment. Answers "which pieces are due this month" — the
//              question a staff member chasing a crown is actually asking.
//
// They are NOT two views of one set. Measured live 2026-09-13, clinic-wide: September
// 2026 held 614 labs by date against 425 by the payroll columns. 189 pieces were
// missing from the month they are handed over in, and no screen in the CRM showed them.
const MODES = [
  { v: 'deduct', label: 'หัก Lab', Icon: Coins, title: 'ตามเดือนที่หักเงินเดือนหมอ', on: 'lab.year / lab.month' },
  { v: 'chase', label: 'ตาม Lab', Icon: PackageSearch, title: 'ตามวันส่งมอบจริง', on: 'lab.date' },
]
// ── % งานแก้ — the quality-control number on this page ───────────────────────
// A remake is unbilled rework: the lab built the piece twice and was paid once. The
// SHARE of remakes, not the count, is what reads as quality — 3 งานแก้ out of 200 is
// noise, 3 out of 12 is a lab to have a conversation about.
//
// ⚠️ Two things the rate does NOT say, both worth knowing before acting on it:
//   • It is not proof of lab fault. A bad impression or prep sends work back too, so
//     regroup by หมอ: if the rate follows the dentist rather than the lab, it is ours.
//   • Under a สถานะ filter it is measured inside that slice only (remakes do not sit
//     evenly across stages), so it stops being a quality figure. The card says so and
//     drops the colour rather than letting the number pass as one.
// The thresholds are a reading aid, NOT clinic policy — no acceptable rate has been set.
// Verified against Xano table 39 on 2026-08-18: 169 rows carry repair == true, clinic-wide
// 2026 = 168 of 5,411 → 3.1%, so 5% reads as "worth a look" rather than as noise.
const REPAIR_WARN = 0.05
const REPAIR_BAD = 0.10
const REPAIR_MIN_N = 5   // under this, one remake swings the rate 20%+ — show it uncoloured
// ⚠️ THE TICKBOX IS NEWER THAN THE DATA. Every repair == true row was created between
// 2026-02-14 and today (verified 2026-08-18); of 38,891 lab rows the flag marks 169, all
// of them 2026. So a pre-2026 year does not report a good lab — it reports a field nobody
// was filling in yet. The rate is withheld for those years instead of printing a 0.0% that
// would read as a clean record.
const REPAIR_FROM_YEAR = 2026
const REPAIR_UNTRACKED = `ช่อง "งานแก้" เริ่มใช้ ก.พ. ${REPAIR_FROM_YEAR} — ปีก่อนหน้าไม่มีข้อมูล ไม่ได้แปลว่าไม่มีงานแก้`
const repairRate = (repair, cases) => (cases ? repair / cases : 0)
const fmtPct = (r) => (r ? `${(r * 100).toFixed(r < 0.1 ? 1 : 0)}%` : '0%')
// Group-by dimensions — drive BOTH the summary card (regroup + retitle) and the ledger's
// in-table group headers. Lab is the default.
const GROUP_OPTS = [
  { v: 'lab', label: 'Lab', title: 'สรุปตาม Lab', Icon: FlaskConical },
  { v: 'date', label: 'วันที่', title: 'สรุปตามวันที่', Icon: Calendar },
  { v: 'doctor', label: 'หมอ', title: 'สรุปตามหมอ', Icon: Stethoscope },
  { v: 'labstage', label: 'สถานะ', title: 'สรุปตามสถานะ', Icon: Layers },
]

// Money on this page is whole baht (kit fmtMoney), with ONE exception: the clinic /
// doctor split of a lab fee. หักทันตแพทย์ is a payroll deduction computed from a
// percentage, so it can carry satang (427.5) — show up to 2 decimals there, only when
// the value has them, rather than rounding a payroll figure on screen.
const SPLIT = { maxDecimals: 2 }

// Filters remembered by the kit (useFilters). ปี/เดือน are date filters, so they go
// back to the current Bangkok month each new day. The patient search box is NOT in
// here: it holds patient names / HN, which never go into browser storage.
const today0 = bkkTodayISO()
const FILTER_DEFAULTS = {
  year: today0.slice(0, 4),
  month: String(Number(today0.slice(5, 7))), // '' = ทั้งปี
  branch: '',
  doctorId: '',
  stage: '',      // labstage_id; '' = all
  labListId: '',  // lablist_id filter (searchable dropdown)
}

const CHASE_BADGE = { overdue: 'rose', late: 'amber', due: 'slate', done: 'emerald' }

// A DataTable row with onRowClick turns Enter / Space into "open the drawer" for any key
// that bubbles up to it — including Space on the checkbox, Enter on the patient link and
// keys on the stage <select>. Controls inside a row stop their keys here (kit gap: the
// row should ignore keys from interactive children).
const stopKeys = (e) => e.stopPropagation()

export default function Lab() {
  const me = useMe()
  const toast = useToast()
  const labOnly = isLabOnly(me)
  const stageScope = labStageScope(me)   // lab | full | staff — see lib/lab.js §stage visibility
  const manager = canManage(me)   // only a manager may run the payroll deduction

  // Which question the page is answering — see §MODES. Persisted: a counter staffer
  // lives in ตาม Lab and a manager in หัก Lab, and neither should have to re-pick it.
  const [mode, setMode] = usePersistedState('lab:mode', 'deduct')
  const chase = mode === 'chase'
  // ตาม Lab opens on the pieces still to chase (labstage <= 5). "ตาม" means follow-up,
  // and on a normal month four rows in five are already received — showing those first
  // buries the handful that need a phone call.
  const [outstandingOnly, setOutstandingOnly] = usePersistedState('lab:outstanding', true)

  // server filters (year/month/branch/doctor bound the fetch) + client filters (stage, lab)
  const { filters, set: setFilter } = useFilters('lab', FILTER_DEFAULTS, { daily: ['year', 'month'] })
  const { year, month, branch, doctorId, stage, labListId } = filters
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  // setX(value) or setX(prev => next), like the useState setters they replace.
  const setter = (k) => (v) => setFilter(k, typeof v === 'function' ? v(filtersRef.current[k]) : v)
  const setYear = setter('year')
  const setMonth = setter('month')
  const setBranch = setter('branch')
  const setDoctorId = setter('doctorId')
  const setStage = setter('stage')
  const setLabListId = setter('labListId')
  const [search, setSearch] = useState('')
  // Sort the summary worst-remake-rate first — the "which lab is letting us down"
  // question. Transient, not persisted: it is a spot check, not how the page reads.
  const [rateSort, setRateSort] = useState(false)
  const [perPage, setPerPage] = usePersistedState('lab:perPage', 50)
  const [groupBy, setGroupBy] = usePersistedState('lab:groupBy', 'lab')   // 'lab' | 'date' | 'doctor' | 'labstage'
  const [sortDir, setSortDir] = usePersistedState('lab:sortDir', 'asc')   // date sort within/among groups
  const [page, setPage] = useState(1)

  // Set when a technician marks a piece LAB ทำงานเสร็จ: { row, resolve } — the stage
  // write waits on the promise and is abandoned if they cancel.
  const [makerAsk, setMakerAsk] = useState(null)

  const [allRows, setAllRows] = useState([])
  const [itemsTotal, setItemsTotal] = useState(0)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Reference lists (labstage / lablist / branches / doctors) + the id maps derived
  // from them — shared with /patient/:id/lab, which needs the identical four.
  const { stages, vendors, doctors, branchName, stageById, vendorMap } = useLabRefs()

  const [selected, setSelected] = useState(() => new Set())
  const [bulkStage, setBulkStage] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  // Bulk stage change moves many labs at once — confirm first (same gate as หักยอดหมอ).
  const [bulkAsk, setBulkAsk] = useState(false)
  const [openRow, setOpenRow] = useState(null)
  // หักยอดหมอ (the payroll deduction) — confirm gate; the result is a toast.
  const [deductAsk, setDeductAsk] = useState(false)
  const [deductBusy, setDeductBusy] = useState(false)

  // Debounce the patient search into the query.
  const [q, setQ] = useState(search)
  useEffect(() => { const t = setTimeout(() => setQ(search), 350); return () => clearTimeout(t) }, [search])

  // A technician's vendor list is the two SSDC benches — the filter dropdown and the
  // grouped summary must not offer labs they can never see a row from.
  const roleVendors = useMemo(
    () => (labOnly ? vendors.filter((v) => isLabOnlyVendor(v.id)) : vendors), [vendors, labOnly])
  // Grouping by Lab or by หมอ says nothing to a technician working two benches, so
  // their picker offers วันที่ + สถานะ only — which makes วันที่ their default.
  const groupOpts = useMemo(
    () => (labOnly ? GROUP_OPTS.filter((o) => o.v === 'date' || o.v === 'labstage') : GROUP_OPTS), [labOnly])
  const doctorOpts = useMemo(() => doctors.map((d) => ({
    id: String(d.id),
    label: d.code || [d.name, d.surname].filter(Boolean).join(' ') || `หมอ #${d.id}`,
  })).sort((a, b) => a.label.localeCompare(b.label, 'th')), [doctors])
  const vendorOpts = useMemo(() => roleVendors.map((v) => ({
    id: String(v.id),
    label: v.name || v.officialName || `Lab #${v.id}`,
  })).sort((a, b) => a.label.localeCompare(b.label, 'th')), [roleVendors])

  const labStageIds = useMemo(() => new Set(stages.filter((s) => s.lab === true).map((s) => Number(s.id))), [stages])
  // REAL stages the row pickers + drawer get — StagePicker does its own per-role masking.
  const roleStages = useMemo(() => (labOnly ? stages.filter((s) => s.lab === true) : stages), [stages, labOnly])
  // What the filter, the counts and the group headers SHOW — for normal staff, LAB รับงาน +
  // LAB ทำงานเสร็จ merge into one "อยู่ที่ LAB" (Ram, 2026-10-18; lib/lab.js §stage visibility).
  const viewStages = useMemo(() => stagesForScope(stages, stageScope), [stages, stageScope])
  // What a stage dropdown may OFFER — never "หักยอดทันตแพทย์แล้ว" (only the payroll
  // action below sets it), and never the merged virtual stage. The filter dropdown still
  // lists every stage: you must be able to look at settled labs, just not move anything
  // into or out of them.
  const pickStages = useMemo(() => selectableStages(viewStages).filter((s) => !s.virtual), [viewStages])
  // The stage that the payroll action consumes — its filter is what reveals the button.
  const pendingStage = useMemo(() => stages.find(isPendingDeductStage), [stages])
  // ⚠️ Gated on the หัก Lab tab. df_doctor/labstage deducts a PAYROLL month (year/month),
  // so offering it under a lab.date window would invite "I filtered September, I deducted
  // September" — and the two Septembers are different sets of 614 and 425 rows.
  const onPendingFilter = !chase && !!pendingStage && String(stage) === String(pendingStage.id)

  // Reset paging + selection on any filter change.
  useEffect(() => { setPage(1) }, [q, stage, labListId, year, month, branch, doctorId, perPage, groupBy, sortDir, mode, outstandingOnly])
  useEffect(() => { setSelected(new Set()) }, [q, stage, labListId, year, month, branch, doctorId, mode])
  // Never sit on a stage filter this role cannot see — a technician on a non-lab stage, or
  // a staffer on a persisted "3"/"4" from before the merge.
  useEffect(() => {
    if (stage && stages.length && !viewStages.some((s) => String(s.id) === String(stage))) setStage('')
  }, [stage, stages.length, viewStages]) // eslint-disable-line react-hooks/exhaustive-deps
  // `me` arrives after first paint, so the persisted default can't know the role —
  // fall back to วันที่ once we learn this is a technician.
  useEffect(() => {
    if (labOnly && !groupOpts.some((o) => o.v === groupBy)) setGroupBy('date')
  }, [labOnly, groupOpts, groupBy, setGroupBy])
  useEffect(() => { if (labOnly && labListId && !isLabOnlyVendor(labListId)) setLabListId('') }, [labOnly, labListId]) // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    // The endpoints take ONE branch_id. For อารีย์ branchFilterIds gives [8, 1] (it also
    // covers head office); the first id is the branch picked — what was sent before.
    const ids = branchFilterIds(branch)
    const common = {
      search: q || undefined,
      branch_id: ids ? ids[0] : undefined,
      doctor_id: doctorId || undefined,
    }
    try {
      // ตาม Lab needs a bounded window (api 652 caps the span), so ทุกปี has no meaning
      // here — the effect below pins a year whenever this tab is open.
      const win = chase ? bkkMonthWindow(year, month) : null
      const res = chase
        ? (win ? await fetchLabByDate({ ...common, from_ms: win.from, to_ms: win.to }) : { rows: [], itemsTotal: 0, truncated: false })
        : await fetchLabForSummary({ ...common, year: year || undefined, month: month || undefined })
      setAllRows(res.rows); setItemsTotal(res.itemsTotal); setTruncated(res.truncated)
    } catch (e) {
      setError(e.message || 'โหลดข้อมูลไม่สำเร็จ'); setAllRows([]); setItemsTotal(0)
    } finally {
      setLoading(false)
    }
  }, [q, year, month, branch, doctorId, chase])
  useEffect(() => { load() }, [load])
  // ทุกปี cannot be windowed — pin the current year rather than showing an empty tab.
  useEffect(() => { if (chase && !year) setYear(bkkTodayISO().slice(0, 4)) }, [chase, year]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live (2026-08-18): `lab_update` fires from the `patient_update - lab` table
  // trigger on every lab row change — crucially including the stage change, which
  // is what this list is FOR and which broadcast nothing at all before. Two people
  // work this worklist at once; one advancing a case now moves the other's screen.
  // Debounced: status_bulk advances a whole selection row by row.
  const liveTimer = useRef(null)
  useEffect(() => () => clearTimeout(liveTimer.current), [])
  useXanoRealtime('lab_update', (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    if (emp != null && emp !== '' && me?.employee_id != null && String(emp) === String(me.employee_id)) return
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(() => load(), 600)
  })

  // First load = spinner. Every later load (filter change, realtime, after a write)
  // keeps the rows on screen, dimmed, until the new set arrives — no blink.
  const firstLoad = loading && !allRows.length

  // Role-scoped fetch set (active only) → lablist filter → stage filter.
  const baseRows = useMemo(() => {
    // lab.active==true only (already enforced by GET /lab; guard here so the rule is explicit).
    const act = allRows.filter((r) => r.active !== false)
    if (!labOnly) return act
    // A technician only works the SSDC benches, and only in their 4 stages.
    return act.filter((r) => labStageIds.has(Number(r.labstage_id)) && isLabOnlyVendor(r.lablist_id))
  }, [allRows, labOnly, labStageIds])
  // Lablist filter (searchable dropdown) narrows the summary + ledger + stage counts.
  const scopeRows = useMemo(() => {
    if (!labListId) return baseRows
    const id = Number(labListId)
    return baseRows.filter((r) => Number(r.lablist_id) === id)
  }, [baseRows, labListId])
  // ยังไม่ได้รับ — ตาม Lab only. Applied BEFORE the stage dropdown so its per-stage
  // counts describe the list you are actually looking at.
  const chaseRows = useMemo(() => {
    if (!chase || !outstandingOnly) return scopeRows
    return scopeRows.filter((r) => !labReceived(r))
  }, [scopeRows, chase, outstandingOnly])
  const perStageCount = useMemo(() => {
    const m = new Map()
    for (const r of chaseRows) { const k = stageKeyFor(r.labstage_id, stageScope); m.set(k, (m.get(k) || 0) + 1) }
    return m
  }, [chaseRows, stageScope])
  const stageRows = useMemo(() => {
    if (!stage) return chaseRows
    const key = String(stage)
    return chaseRows.filter((r) => stageKeyFor(r.labstage_id, stageScope) === key)
  }, [chaseRows, stage, stageScope])

  // The chase tallies — counted over the WHOLE window (scopeRows), not the filtered
  // ledger, so the two toggle chips keep saying how much work exists either way.
  const chaseCounts = useMemo(() => {
    let outstanding = 0, overdue = 0, late = 0, due = 0
    for (const r of scopeRows) {
      if (labReceived(r)) continue
      outstanding += 1
      const lv = labChaseState(r).level
      if (lv === 'overdue') overdue += 1
      else if (lv === 'late') late += 1
      else due += 1
    }
    return { outstanding, overdue, late, due, total: scopeRows.length, received: scopeRows.length - outstanding }
  }, [scopeRows])
  // Picking a received stage (6+) while ยังไม่ได้รับ is on would show an empty list for
  // no visible reason. Release the toggle instead of explaining the contradiction.
  useEffect(() => {
    if (chase && outstandingOnly && stage && Number(stage) >= 6) setOutstandingOnly(false)
  }, [chase, outstandingOnly, stage, setOutstandingOnly])

  // Why the rate cannot be trusted right now, if it cannot: an unfilled year beats a
  // stage slice, since one withholds the number and the other only decolours it.
  const rateCaveat = year && Number(year) < REPAIR_FROM_YEAR ? 'year' : stage ? 'stage' : ''
  // The per-group % column is GONE from the table (Ram, 2026-08-18) — the rate lives in
  // the header tile only. This flag now gates the rate SORT, for the same reason the
  // column was refused under สรุปตามวันที่: a day holds one to six pieces, so its rate can
  // only be 0% / 33% / 50% / 100%, and ordering Tuesdays by that is ordering noise.
  // Quality is a property of a Lab or a หมอ, not of a date.
  const canSortByRate = groupBy !== 'date'

  const summaryMoney = useMemo(() => {
    let total = 0, clinic = 0, doctor = 0, repair = 0
    for (const r of stageRows) {
      total += labPrice(r); clinic += labPriceClinic(r); doctor += labPriceDoctor(r)
      if (r.repair) repair += 1
    }
    // A technician plans their bench by งานใหม่ vs งานแก้ — a remake is urgent and
    // unbilled work, so the split matters more to them than any single total.
    return { total, clinic, doctor, cases: stageRows.length, repair, fresh: stageRows.length - repair }
  }, [stageRows])

  // Ledger = the filtered set. All narrowing is done by the filters above (doctor/branch/
  // year/month at fetch; lablist + stage here), so the ledger is exactly stageRows.
  const ledger = stageRows
  const ledgerMoney = summaryMoney

  // groupInfo(r): the group key + label for the current groupBy — drives BOTH the summary
  // card and the ledger's in-table group headers. filterVal = what a summary click filters by.
  const groupInfo = useCallback((r) => {
    if (groupBy === 'lab') { const id = Number(r.lablist_id) || 0; return { key: 'l' + id, label: vendorMap.get(id)?.name || r.lablist?.name || (id ? `#${id}` : 'ไม่ระบุ Lab'), filterVal: String(id), inhouse: !!vendorMap.get(id)?.inhouse } }
    if (groupBy === 'doctor') return { key: 'd' + (Number(r.doctor_id) || 0), label: labDoctorCode(r) || 'ไม่ระบุหมอ', filterVal: String(Number(r.doctor_id) || 0) }
    if (groupBy === 'labstage') {
      const k = stageKeyFor(r.labstage_id, stageScope)
      const s = shownStage(r.labstage_id, stages, stageScope) || r.labstage || {}
      return { key: 's' + k, label: stageShort(s.name) || 'ไม่ระบุสถานะ', color: s.color, filterVal: k }
    }
    return { key: fmtDate(r.date), label: fmtDate(r.date), filterVal: '', ts: Number(r.date) || 0 }
  }, [groupBy, vendorMap, stages, stageScope])

  // Summary = stageRows grouped by the current dimension, sorted by money. Retitled +
  // regrouped whenever groupBy changes. (Hidden for a technician — see the render.)
  const summaryRows = useMemo(() => {
    const m = new Map()
    for (const r of stageRows) {
      const gi = groupInfo(r)
      let s = m.get(gi.key)
      if (!s) { s = { key: gi.key, label: gi.label, color: gi.color, inhouse: gi.inhouse, filterVal: gi.filterVal, ts: gi.ts || 0, cases: 0, price: 0, repair: 0 }; m.set(gi.key, s) }
      s.cases += 1; s.price += labPrice(r)
      if (r.repair) s.repair += 1
    }
    const arr = [...m.values()]
    if (rateSort && canSortByRate) {
      // Worst first — but a lab with 1 piece and 1 remake is not the worst lab, it is
      // one bad day. Groups too thin to judge sink below the ones with real volume.
      arr.sort((a, b) => {
        const ja = a.cases >= REPAIR_MIN_N ? 1 : 0, jb = b.cases >= REPAIR_MIN_N ? 1 : 0
        return (jb - ja)
          || (repairRate(b.repair, b.cases) - repairRate(a.repair, a.cases))
          || (b.cases - a.cases)
      })
    } else if (groupBy === 'date') {
      const dir = sortDir === 'asc' ? 1 : -1
      arr.sort((a, b) => dir * ((a.ts || 0) - (b.ts || 0)))
    } else {
      arr.sort((a, b) => (labOnly ? b.cases - a.cases : b.price - a.price) || String(a.label).localeCompare(String(b.label), 'th'))
    }
    return arr
  }, [stageRows, groupInfo, labOnly, groupBy, sortDir, rateSort, canSortByRate])
  const maxSummaryMetric = useMemo(() => Math.max(1, ...summaryRows.map((s) => (labOnly ? s.cases : s.price))), [summaryRows, labOnly])

  // A summary-row click sets the matching filter (date has none). Also drives the highlight.
  const activeGroupFilter = groupBy === 'lab' ? labListId : groupBy === 'doctor' ? doctorId : groupBy === 'labstage' ? stage : ''
  const pickGroup = (s) => {
    if (groupBy === 'lab') setLabListId((v) => (String(v) === s.filterVal ? '' : s.filterVal))
    else if (groupBy === 'doctor') setDoctorId((v) => (String(v) === s.filterVal ? '' : s.filterVal))
    else if (groupBy === 'labstage') setStage((v) => (String(v) === s.filterVal ? '' : s.filterVal))
  }
  const groupOpt = groupOpts.find((o) => o.v === groupBy) || groupOpts[0]
  const orderedLedger = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const rows = [...ledger].sort((a, b) => (Number(a.date) - Number(b.date)) * dir)
    if (groupBy === 'date') return rows
    const groups = new Map()
    for (const r of rows) {
      const k = groupInfo(r).key
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k).push(r)
    }
    const keys = [...groups.keys()]
    if (groupBy === 'labstage') keys.sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))  // workflow order
    else keys.sort((a, b) => groupInfo(groups.get(a)[0]).label.localeCompare(groupInfo(groups.get(b)[0]).label, 'th'))
    return keys.flatMap((k) => groups.get(k))
  }, [ledger, sortDir, groupBy, groupInfo])
  const groupCounts = useMemo(() => {
    const m = new Map()
    for (const r of orderedLedger) { const k = groupInfo(r).key; m.set(k, (m.get(k) || 0) + 1) }
    return m
  }, [orderedLedger, groupInfo])

  // Pagination.
  const pageCount = Math.max(1, Math.ceil(ledger.length / perPage))
  const safePage = Math.min(Math.max(1, page), pageCount)
  const pageRows = useMemo(() => orderedLedger.slice((safePage - 1) * perPage, safePage * perPage), [orderedLedger, safePage, perPage])

  // Selection / bulk (over the whole filtered ledger, like the reimbursement page).
  // Settled rows (หักยอดทันตแพทย์แล้ว) are excluded from selection entirely — a bulk
  // change must not be able to reach what a single picker refuses.
  const isLocked = useCallback((r) => isStageLockedRow(r, stageById), [stageById])
  const ledgerIds = useMemo(() => ledger.filter((r) => !isLocked(r)).map((r) => r.id).filter((x) => x != null), [ledger, isLocked])
  const allSelected = ledgerIds.length > 0 && ledgerIds.every((id) => selected.has(id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(ledgerIds))
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  // A technician marking a piece LAB ทำงานเสร็จ must first say who made it — the
  // stage write waits on this promise and is abandoned if they cancel. Everyone
  // else moves stages unimpeded.
  const askMakerIfDone = useCallback((row, nextStageId) => {
    if (!labOnly || Number(nextStageId) !== STAGE_LAB_DONE_ID) return true
    return new Promise((resolve) => setMakerAsk({ row, resolve }))
  }, [labOnly])

  const patchRowStage = useCallback((labId, newStageId) => {
    setAllRows((rows) => rows.map((r) => (r.id === labId ? applyStagePatch(r, newStageId, stageById, me?.employee_id) : r)))
    setOpenRow((o) => (o && o.id === labId
      ? applyStagePatch(o, newStageId, stageById, me?.employee_id, { appendLog: true }) : o))
  }, [stageById, me])

  const priceLocked = useCallback((row) => isPriceLockedRow(row, stageById), [stageById])

  // The drawer's ONE write (2026-08-17). ค่า Lab, ข้อมูล Lab and production_type/retainer
  // used to be three inline forms with three บันทึก buttons — two of which could be open
  // at once. They were always three doors onto the same upstream POST /lab, so the drawer
  // now sends the lot in a single call: functions/api/lab/edit.js. Optimistic; resolves
  // the new lablist name + doctor code/img from the already-loaded reference lists.
  // Throws on failure so the drawer can show an inline error.
  const saveLab = useCallback(async (row, payload) => {
    await postLabEdit(row, payload)
    const apply = (x) => (x && x.id === row.id ? applyEditPatch(x, payload, { vendorMap, doctors }) : x)
    setAllRows((rows) => rows.map(apply))
    setOpenRow((o) => apply(o))
  }, [vendorMap, doctors])

  // The confirm stays open (busy) while the write runs, so a second click cannot
  // send it twice. Failure → a toast; the list stays on screen.
  async function applyBulk() {
    const ids = [...selected]
    if (!ids.length || !bulkStage) { setBulkAsk(false); return }
    setBulkBusy(true)
    try {
      await postJson('/api/lab/status_bulk', { lab_id: ids, labstage_id: Number(bulkStage) })
      setBulkAsk(false)
      setSelected(new Set()); setBulkStage('')
      toast.success(`เปลี่ยนสถานะ ${fmtNumber(ids.length)} รายการแล้ว`)
      await load()
    } catch (e) {
      setBulkAsk(false)
      toast.error('เปลี่ยนสถานะหลายรายการไม่สำเร็จ', { detail: String(e?.message || e) })
    } finally {
      setBulkBusy(false)
    }
  }

  // ── หักยอดค่า LAB จากเงินเดือนทันตแพทย์ ────────────────────────────────────────
  // NOT a stage change: POST /api/lab/df-labstage → Xano df_doctor/labstage walks
  // the salary_doctor rows of that month, marks them live and moves the labs THEY
  // list to หักยอดทันตแพทย์แล้ว. So it is scoped by เดือน/ปี (+ the หมอ filter when
  // set) — never by the selected rows — and labs missing from the payroll summary
  // stay in รอสรุปหักหมอ on purpose. Irreversible; refetch afterwards because the
  // set of rows that moved is decided upstream, not here.
  const deductDoctorLabel = doctorId ? (doctorOpts.find((d) => d.id === String(doctorId))?.label || `หมอ #${doctorId}`) : ''
  const canDeduct = manager && onPendingFilter && !!year && !!month
  const monthLabel = month ? TH_MONTHS_LONG[Number(month) - 1] : ''
  async function runDeduct() {
    setDeductBusy(true)
    try {
      const res = await postJson('/api/lab/df-labstage', {
        year: Number(year),
        month: Number(month),
        doctor_id: doctorId ? Number(doctorId) : 0,
      })
      setDeductAsk(false)
      setSelected(new Set())
      toast.success(typeof res === 'string' && res ? res : 'หักยอดค่า LAB เรียบร้อย')
      await load()
    } catch (e) {
      setDeductAsk(false)
      toast.error('หักยอดค่า LAB ไม่สำเร็จ', { detail: String(e?.message || e) })
    } finally {
      setDeductBusy(false)
    }
  }

  // ล้างตัวกรอง clears what narrows the list; ปี/เดือน are the period, not a filter,
  // and stay where they are (as before).
  const anyFilter = !!(search || stage || labListId || branch || doctorId)
  function clearFilters() { setSearch(''); setFilter({ stage: '', labListId: '', branch: '', doctorId: '' }) }

  // ── option lists for the kit dropdowns ──
  const yearOptions = useMemo(() => [{ value: '', label: 'ทุกปี' }, ...LAB_YEARS.map((y) => ({ value: String(y), label: String(y) }))], [])
  const monthOptions = useMemo(() => [{ value: '', label: 'ทั้งปี' }, ...TH_MONTHS_LONG.map((m, i) => ({ value: String(i + 1), label: m }))], [])
  const doctorOptions = useMemo(() => [{ value: '', label: 'ทุกหมอ' }, ...doctorOpts.map((d) => ({ value: d.id, label: d.label }))], [doctorOpts])
  const vendorOptions = useMemo(() => [{ value: '', label: 'ทุก Lab' }, ...vendorOpts.map((d) => ({ value: d.id, label: d.label }))], [vendorOpts])
  const stageOptions = useMemo(() => [
    { value: '', label: 'ทุกสถานะ' },
    ...viewStages.map((s) => ({ value: String(s.id), label: stageShort(s.name), count: perStageCount.get(String(s.id)) || 0 })),
  ], [viewStages, perStageCount])
  const bulkStageOptions = useMemo(() => pickStages.map((s) => ({ value: String(s.id), label: stageShort(s.name) })), [pickStages])
  const perPageOptions = useMemo(() => PER_PAGE_OPTS.map((n) => ({ value: n, label: `${n} / หน้า` })), [])

  // ── summary table (grouped) ──
  const summaryColumns = useMemo(() => [
    {
      key: 'label', label: groupOpt.label,
      render: (s) => {
        const active = groupBy !== 'date' && s.filterVal !== '' && String(activeGroupFilter) === s.filterVal
        return (
          <span className="inline-flex items-center gap-2 min-w-0 max-w-full">
            {groupBy === 'labstage' && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color || '#94a3b8' }} />}
            <span className="text-slate-700 truncate">{s.label}</span>
            {s.inhouse && <Badge tone="brand">In-house</Badge>}
            {active && <Badge tone="brand" dot>กำลังดู</Badge>}
          </span>
        )
      },
    },
    { key: 'cases', label: 'งาน', align: 'right', width: '80px', render: (s) => <span className="text-slate-500">{fmtNumber(s.cases)}</span> },
    {
      // a zero is muted so a group with remakes stands out down the column
      key: 'repair', label: <span className="text-amber-600">งานแก้</span>, align: 'right', width: '84px',
      render: (s) => <span className={s.repair ? 'text-amber-600 font-medium' : 'text-slate-300'}>{fmtNumber(s.repair)}</span>,
    },
    {
      key: 'price', label: <span style={{ color: INDIGO }}>ค่า Lab</span>, align: 'right', width: '130px',
      render: (s) => <span style={s.price ? { color: INDIGO } : undefined}>{fmtMoney(s.price, { zero: '—' })}</span>,
    },
    {
      key: 'share', label: 'สัดส่วน', width: '220px',
      render: (s) => <Bar frac={(labOnly ? s.cases : s.price) / maxSummaryMetric} color={s.color || INDIGO} />,
    },
  ], [groupOpt, groupBy, activeGroupFilter, labOnly, maxSummaryMetric])
  const summaryTable = useTableState(summaryRows, summaryColumns, { pageSize: 50 })

  // ── ledger table ──
  const ledgerColumns = useMemo(() => [
    {
      key: 'select', label: '', width: '44px',
      render: (e) => {
        const locked = isLocked(e)
        return (
          <span className="inline-flex" onClick={(ev) => ev.stopPropagation()} onKeyDown={stopKeys}
            title={locked ? 'หักยอดหมอแล้ว — เปลี่ยนสถานะไม่ได้' : undefined}>
            <Checkbox checked={selected.has(e.id)} onChange={() => { if (!locked) toggleOne(e.id) }}
              disabled={e.id == null || locked} aria-label="เลือกรายการนี้" />
          </span>
        )
      },
    },
    {
      key: 'date', label: 'วันที่',
      render: (e) => (
        <span className="text-slate-600 tabular-nums whitespace-nowrap">
          {fmtDate(e.date)}
          {/* Both dates, once the tab is about lateness — a row flagged
              "เลยกำหนดส่ง" is unreadable without the กำหนดส่ง it is late
              against. Hidden when they agree, which is the common case. */}
          {chase && Number(e.deadline) > 0 && Number(e.deadline) !== Number(e.date) && (
            <span className="block text-[11.5px] text-slate-400 leading-tight">กำหนด {fmtDate(e.deadline)}</span>
          )}
        </span>
      ),
    },
    ...(chase ? [{
      key: 'chase', label: 'การรับ',
      render: (e) => { const st = labChaseState(e); return <Badge tone={CHASE_BADGE[st.level] || 'slate'} dot>{st.label}</Badge> },
    }] : []),
    {
      // name / HN open the patient's Lab tab in a new tab; the row opens the drawer
      key: 'patient', label: 'คนไข้',
      render: (e) => (
        <div className="flex items-center gap-2 whitespace-nowrap" onKeyDown={stopKeys}>
          <PatientName inline context="lab"
            patient={{ id: e.patient_id ?? e._patient?.id, fullname: labPatientName(e), HN: labPatientHN(e) || undefined }} />
          {e.repair && <Badge tone="amber"><Wrench size={10} /> แก้</Badge>}
          {hasRetainer(e.retainer) && <Badge tone="violet">RTN</Badge>}
        </div>
      ),
    },
    {
      key: 'doctor', label: 'หมอ',
      render: (e) => {
        const docImg = labDoctorImg(e)
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {docImg ? <img src={docImg} alt="" className="w-6 h-6 rounded-full object-cover shrink-0 bg-slate-100 border border-slate-200" /> : <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[12px] font-semibold shrink-0">{nameInitial(labDoctorCode(e))}</span>}
            <span className="text-slate-600">{labDoctorCode(e)}</span>
          </span>
        )
      },
    },
    { key: 'lab', label: 'Lab', render: (e) => <span className="whitespace-nowrap text-slate-500">{labVendorName(e)}</span> },
    ...(!labOnly ? [{
      key: 'price', label: 'ค่า Lab', align: 'right',
      // no price object on the row = unknown → '—', never 0
      render: (e) => <span className="font-medium text-slate-800">{e.price?.price == null ? '—' : fmtMoney(labPrice(e))}</span>,
    }] : []),
    {
      key: 'stage', label: 'สถานะ',
      render: (e) => <div onClick={(ev) => ev.stopPropagation()} onKeyDown={stopKeys}><StagePicker row={e} stages={roleStages} onChanged={patchRowStage} beforeChange={askMakerIfDone} /></div>,
    },
  ], [chase, labOnly, selected, isLocked, roleStages, patchRowStage, askMakerIfDone]) // eslint-disable-line react-hooks/exhaustive-deps

  const ledgerGroupHeader = (k, rows) => {
    const gi = groupInfo(rows[0])
    return (
      <span className="inline-flex items-center gap-1.5">
        {groupBy === 'date' && <Calendar size={13} className="text-slate-400" />}
        {groupBy === 'doctor' && <Stethoscope size={13} className="text-slate-400" />}
        {groupBy === 'labstage' && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: gi.color || '#94a3b8' }} />}
        {gi.label}
        <span className="text-slate-400 font-normal tabular-nums">· {fmtNumber(groupCounts.get(k) || 0)}</span>
      </span>
    )
  }

  const techTiles = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2">
      <SumTile label="จำนวนงาน" value={summaryMoney.cases} strong count />
      <SumTile label="งานธรรมดา" value={summaryMoney.fresh} color="#0891b2" count />
      <SumTile label="งานแก้" value={summaryMoney.repair} color="#d97706" count />
      <RateTile repair={summaryMoney.repair} cases={summaryMoney.cases} caveat={rateCaveat} />
    </div>
  )

  return (
    <Page>
      <PageHeader
        icon={FlaskConical}
        title="Lab"
        subtitle={labOnly ? 'ติดตามและอัปเดตสถานะงาน Lab' : 'ติดตามงาน Lab ทุกขั้นตอน · เปลี่ยนสถานะ · สรุปตาม Lab'}
      >
        {/* หัก Lab / ตาม Lab — the ปี/เดือน filter below means a different column in each.
            The line under the tabs names that column out loud: two tabs that look identical
            and disagree by 189 rows a month have to say WHY, or the next person calls it a bug. */}
        <Tabs
          items={MODES.map(({ v, label, Icon, title }) => ({ key: v, label, icon: Icon, title }))}
          value={mode}
          onChange={setMode}
        />
        <p className="mt-2 text-[12.5px] text-slate-500">
          {chase
            ? <>กรองตาม <span className="font-medium text-slate-700">วันส่งมอบจริง (lab.date)</span> — งานที่ต้องเข้าคลินิกในเดือนนี้</>
            : <>กรองตาม <span className="font-medium text-slate-700">เดือนที่หักเงินเดือนหมอ</span> — ไม่ใช่วันส่งมอบ</>}
        </p>
      </PageHeader>

      {/* Filters */}
      <FilterCard onClear={clearFilters} canClear={anyFilter}>
        <Field label="ปี" className="min-w-[110px]">
          <Select value={year} onChange={setYear} options={yearOptions} placeholder="ทุกปี" />
        </Field>
        <Field label="เดือน" className="min-w-[130px]">
          <Select value={month} onChange={setMonth} options={monthOptions} placeholder="ทั้งปี" />
        </Field>
        <Field label="สาขา" className="min-w-[140px]">
          <BranchSelect value={branch} onChange={setBranch} />
        </Field>
        <Field label="หมอ" className="min-w-[160px]">
          <SearchSelect value={doctorId} onChange={setDoctorId} options={doctorOptions} placeholder="ทุกหมอ" />
        </Field>
        <Field label="Lab" className="min-w-[170px]">
          <SearchSelect value={labListId} onChange={setLabListId} options={vendorOptions} placeholder="ทุก Lab" />
        </Field>
        <Field label="สถานะ" className="min-w-[190px]">
          <Select value={stage} onChange={setStage} options={stageOptions} placeholder="ทุกสถานะ" />
        </Field>
        <Field label="ค้นหาคนไข้ / HN" className="flex-1 min-w-[180px]">
          <SearchInput value={search} onChange={setSearch} placeholder="ชื่อ หรือ HN…" />
        </Field>

        {/* view controls — how the list is drawn, not what it holds */}
        <div className="basis-full border-t border-slate-100 pt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px]">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">จัดกลุ่ม</span>
            <Segmented size="sm" value={groupBy} onChange={setGroupBy}
              options={groupOpts.map(({ v, label, Icon }) => ({ value: v, label, icon: Icon }))} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">เรียงวันที่</span>
            <Button size="sm" variant="secondary" icon={sortDir === 'asc' ? ArrowUp : ArrowDown}
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
              {sortDir === 'asc' ? 'เก่า → ใหม่' : 'ใหม่ → เก่า'}
            </Button>
          </div>
          {chase && (
            <div className="flex items-center gap-2">
              <span className="text-slate-500">แสดง</span>
              <Segmented size="sm" value={outstandingOnly ? 'out' : 'all'} onChange={(v) => setOutstandingOnly(v === 'out')}
                options={[
                  { value: 'out', label: 'ยังไม่ได้รับ', count: fmtNumber(chaseCounts.outstanding) },
                  { value: 'all', label: 'ทั้งหมด', count: fmtNumber(chaseCounts.total) },
                ]} />
            </div>
          )}
        </div>
      </FilterCard>

      {truncated && (
        <Banner tone="warn" className="mb-4">
          แสดง {fmtNumber(allRows.length)} จาก {fmtNumber(itemsTotal)} รายการ — เลือกเดือน/สาขา/หมอ เพื่อดูให้ครบ
        </Banner>
      )}

      {/* ตาม Lab — the report that did not exist. Four numbers, in the order a staff
          member acts on them: what the patient already came for, what has slipped its
          internal target, what is still fine, what is in. Clicking a tile filters. */}
      {chase && !firstLoad && !error && (
        <div className={`mb-4 grid grid-cols-2 lg:grid-cols-4 gap-3 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <ChaseTile tone="overdue" label="เลยวันนัดแล้ว" hint="คนไข้ถึงวันนัดแล้ว ของยังไม่เข้า"
            value={chaseCounts.overdue} onClick={() => setOutstandingOnly(true)} />
          <ChaseTile tone="late" label="เลยกำหนดส่ง" hint="ยังไม่ถึงวันนัด แต่เลยกำหนดส่งแล้ว"
            value={chaseCounts.late} onClick={() => setOutstandingOnly(true)} />
          <ChaseTile tone="due" label="ยังไม่ถึงกำหนด" hint="อยู่ในกำหนด ไม่ต้องตาม"
            value={chaseCounts.due} onClick={() => setOutstandingOnly(true)} />
          <ChaseTile tone="done" label="ได้รับแล้ว" hint="อยู่ที่คลินิกแล้ว"
            value={chaseCounts.received} onClick={() => setOutstandingOnly(false)} />
        </div>
      )}

      {/* Summary — grouped by the selected dimension (Lab / วันที่ / หมอ / สถานะ).
          Hidden for a technician: scoped to two benches and stripped of cost, it just
          restates the ledger below it. Its count tiles move to the ledger header. */}
      {!labOnly && !error && (
        <section className="mb-6">
          <SectionHead
            icon={groupOpt.Icon}
            title={groupOpt.title}
            badge={summaryRows.length ? `${fmtNumber(summaryRows.length)} กลุ่ม` : undefined}
            subtitle={[
              rateSort && canSortByRate ? 'เรียงตาม % งานแก้ มาก → น้อย'
                : groupBy === 'date' ? `ภาพรวมตามวันที่ · ${sortDir === 'asc' ? 'เก่า→ใหม่' : 'ใหม่→เก่า'}`
                  : 'คลิกแถวเพื่อกรองรายการด้านล่าง',
              rateCaveat === 'year' ? `% งานแก้ ใช้ได้ตั้งแต่ปี ${REPAIR_FROM_YEAR}`
                : rateCaveat === 'stage' ? '% งานแก้ นับเฉพาะสถานะที่กรอง — ยังไม่ใช่ตัวเลขคุณภาพ' : '',
            ].filter(Boolean).join(' · ')}
            right={
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2">
                <SumTile label="ค่า Lab รวม" value={summaryMoney.total} color={INDIGO} strong />
                <SumTile label="หักคลินิก" value={summaryMoney.clinic} color="#0891b2" split />
                <SumTile label="หักทันตแพทย์" value={summaryMoney.doctor} color="#7c3aed" split />
                {/* No clinic-wide งานแก้ tile here (Ram, 2026-08-18). It duplicated the count
                    the % tile already carries on its own subline ("19 / 319 ชิ้น"), and the
                    cut worth reading is per Lab / per หมอ — which is why งานแก้ is a COLUMN
                    in the table below instead. The % stays: a rate has no per-group column. */}
                <RateTile repair={summaryMoney.repair} cases={summaryMoney.cases} caveat={rateCaveat}
                  onSort={canSortByRate ? () => setRateSort((v) => !v) : null} sorted={rateSort && canSortByRate} />
              </div>
            }
          />
          <DataTable
            columns={summaryColumns}
            {...summaryTable.tableProps}
            rowKey={(s) => s.key}
            onRowClick={groupBy !== 'date' ? pickGroup : undefined}
            rowClassName={(s) => (groupBy !== 'date' && s.filterVal !== '' && String(activeGroupFilter) === s.filterVal ? 'bg-brand-50/60' : '')}
            loading={loading}
            filtered={anyFilter}
            onClearFilters={clearFilters}
            empty={{ title: 'ไม่พบรายการ Lab ในช่วงและตัวกรองที่เลือก' }}
            fixed
          />
        </section>
      )}

      {/* Ledger */}
      <section>
        <SectionHead
          icon={FileText}
          title="รายการ Lab"
          badge={!firstLoad ? `${fmtNumber(ledger.length)} รายการ` : undefined}
          subtitle={`จัดกลุ่มตาม${groupOpt.label} · ${sortDir === 'asc' ? 'เก่า→ใหม่' : 'ใหม่→เก่า'}`}
          right={labOnly && !firstLoad && !error ? techTiles : undefined}
        />

        {/* หักยอดหมอ — only while the รอสรุปหักหมอ filter is on (this action is
            what that stage exists for). Managers get the button; everyone else
            gets the same explanation without it, so nobody hunts for a missing
            stage option. */}
        {onPendingFilter && (
          <div className="mb-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-violet-800"><Coins size={15} /> หักยอดค่า LAB จากเงินเดือนทันตแพทย์</span>
              <div className="flex-1" />
              {manager ? (
                <Button size="sm" icon={Coins} onClick={() => setDeductAsk(true)} loading={deductBusy}
                  disabled={!canDeduct}
                  disabledReason={!month ? 'เลือกเดือนก่อน (หักยอดได้ทีละเดือน)' : !year ? 'เลือกปีก่อน' : undefined}>
                  หักยอดหมอ
                </Button>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[12px] text-violet-700"><Lock size={12} /> เฉพาะผู้จัดการ</span>
              )}
            </div>
            <p className="mt-1.5 text-[12px] text-violet-700 leading-relaxed">
              ทำทีละเดือน — ระบบจะหักค่า LAB ที่อยู่ในสรุปเงินเดือนหมอของ
              <span className="font-medium"> {month ? monthLabel : '— เลือกเดือน —'} {year ? Number(year) : ''}</span>
              {deductDoctorLabel ? <> เฉพาะ <span className="font-medium">{deductDoctorLabel}</span></> : ' ทุกหมอ'}
              {' '}แล้วเปลี่ยนสถานะเป็น “หักหมอแล้ว” · <span className="font-medium">เปลี่ยนกลับไม่ได้</span>
              {!month && ' · เลือกเดือนก่อน'}
            </p>
          </div>
        )}

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2.5">
            <span className="text-[13px] font-medium text-brand-800 inline-flex items-center gap-1.5"><Layers size={15} /> เลือก {fmtNumber(selected.size)} รายการ</span>
            <div className="flex-1" />
            <div className="min-w-[200px]">
              <Select value={bulkStage} onChange={setBulkStage} options={bulkStageOptions} placeholder="เปลี่ยนสถานะเป็น…" />
            </div>
            <Button size="sm" onClick={() => setBulkAsk(true)} loading={bulkBusy} disabled={!bulkStage}
              disabledReason={!bulkStage ? 'เลือกสถานะก่อน' : undefined}>
              ยืนยัน
            </Button>
            <Button size="sm" variant="ghost" icon={X} onClick={() => setSelected(new Set())}>ล้าง</Button>
          </div>
        )}

        {!error && !firstLoad && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2 text-[12.5px]">
            <div className="flex items-center gap-3">
              <button type="button" onClick={toggleAll} disabled={!ledgerIds.length} className="text-brand-600 hover:underline disabled:text-slate-300 disabled:no-underline">{allSelected ? 'ยกเลิกเลือกทั้งหมด' : `เลือกทั้งหมดที่กรอง (${fmtNumber(ledgerIds.length)})`}</button>
              <div className="w-[118px]">
                <Select value={perPage} onChange={(v) => setPerPage(Number(v))} options={perPageOptions} />
              </div>
            </div>
            {!labOnly && (
              <div className="flex items-center gap-3 text-slate-500">
                <span>คลินิก <span className="tabular-nums font-medium text-cyan-700">฿{fmtMoney(ledgerMoney.clinic, SPLIT)}</span></span>
                <span>หมอ <span className="tabular-nums font-medium text-violet-700">฿{fmtMoney(ledgerMoney.doctor, SPLIT)}</span></span>
                <span>ค่า Lab รวม <span className="tabular-nums font-semibold text-slate-800">฿{fmtMoney(ledgerMoney.total)}</span></span>
              </div>
            )}
          </div>
        )}

        <DataTable
          columns={ledgerColumns}
          rows={pageRows}
          rowKey={(e, i) => e.id ?? `i${i}`}
          onRowClick={setOpenRow}
          rowClassName={(e) => (selected.has(e.id) ? 'bg-brand-50/40' : '')}
          groupBy={(e) => groupInfo(e).key}
          groupHeader={ledgerGroupHeader}
          loading={loading}
          error={error}
          onRetry={load}
          filtered={anyFilter || (chase && outstandingOnly)}
          onClearFilters={() => { clearFilters(); if (chase) setOutstandingOnly(false) }}
          empty={{ title: 'ไม่พบรายการ Lab' }}
          page={safePage}
          pageTotal={pageCount}
          onPageChange={setPage}
          total={ledger.length}
          minWidth={labOnly ? 760 : 880}
        />
      </section>

      {openRow && (
        <LabDetailDrawer
          row={openRow}
          stages={roleStages}
          branchName={branchName}
          hideMoney={labOnly}
          priceLocked={priceLocked(openRow)}
          onSave={saveLab}
          onClose={() => setOpenRow(null)}
          onChanged={patchRowStage}
          beforeStageChange={askMakerIfDone}
          readOnlyDetail={labOnly}
          labOnly={labOnly}
        />
      )}

      {makerAsk && (
        <MakerPicker
          row={makerAsk.row}
          me={me}
          onSaved={(makerId) => {
            const labId = makerAsk.row.id
            const patch = (r) => (r.id === labId
              ? { ...r, retainer: { ...(r.retainer || {}), maker: makerId } } : r)
            setAllRows((rs) => rs.map(patch))
            setOpenRow((r) => (r && r.id === labId ? patch(r) : r))
            makerAsk.resolve(true)
            setMakerAsk(null)
          }}
          onCancel={() => { makerAsk.resolve(false); setMakerAsk(null) }}
        />
      )}

      {bulkAsk && (
        <ConfirmDialog
          title={`เปลี่ยนสถานะ ${selected.size} รายการ?`}
          message={`Lab ที่เลือกทั้ง ${selected.size} รายการ จะถูกเปลี่ยนสถานะเป็น “${stageShort(pickStages.find((s) => String(s.id) === String(bulkStage))?.name || '')}”`}
          confirmLabel="ยืนยันเปลี่ยนสถานะ"
          busy={bulkBusy}
          onConfirm={applyBulk}
          onCancel={() => setBulkAsk(false)}
        />
      )}

      {deductAsk && (
        <ConfirmDialog
          tone="danger"
          title="หักยอดค่า LAB จากเงินเดือนทันตแพทย์?"
          message={`ค่า LAB ในสรุปเงินเดือนของ ${monthLabel} ${year ? Number(year) : ''}${deductDoctorLabel ? ` (เฉพาะ ${deductDoctorLabel})` : ' (ทุกหมอ)'} จะถูกหักจากเงินเดือนหมอ และ Lab เหล่านั้นจะเปลี่ยนเป็น “หักหมอแล้ว” ถาวร — เปลี่ยนสถานะกลับไม่ได้ ตรวจยอดให้เรียบร้อยก่อนยืนยัน`}
          confirmLabel="ยืนยันเปลี่ยนสถานะ"
          busy={deductBusy}
          onConfirm={runDeduct}
          onCancel={() => setDeductAsk(false)}
        />
      )}
    </Page>
  )
}

/* ── presentational (page-specific widgets the kit does not have) ───────────── */
// Section heading over a table: icon tile · title · count · one-line subtitle, with the
// figure tiles on the right. (Kit gap: there is no titled section/card shell.)
function SectionHead({ icon: Icon, title, badge, subtitle, right }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Icon size={17} strokeWidth={2} /></span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-slate-900 leading-tight flex items-center gap-2">
            {title}
            {badge && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-medium text-slate-500">{badge}</span>}
          </h2>
          {subtitle && <p className="text-[12.5px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  )
}
function Bar({ frac, color, thin }) {
  return (
    <div className={`${thin ? 'h-1.5 bg-slate-100' : 'h-2 bg-slate-200/70'} rounded-full overflow-hidden`}>
      <div className={`h-full rounded-full ${thin ? 'opacity-70' : ''}`} style={{ width: `${Math.round(Math.min(1, Math.max(0, frac || 0)) * 100)}%`, background: color }} />
    </div>
  )
}
// ── ตาม Lab presentational bits ──────────────────────────────────────────────
const CHASE_TILE = {
  overdue: 'border-rose-200 bg-rose-50 text-rose-700',
  late: 'border-amber-200 bg-amber-50 text-amber-700',
  due: 'border-slate-200 bg-white text-slate-600',
  done: 'border-emerald-200 bg-emerald-50 text-emerald-700',
}
function ChaseTile({ tone, label, hint, value, onClick }) {
  return (
    <button type="button" onClick={onClick} title={hint}
      className={`rounded-xl border px-4 py-3 text-left transition hover:shadow-sm ${CHASE_TILE[tone]}`}>
      <div className="text-[24px] font-bold tabular-nums leading-none">{fmtNumber(value)}</div>
      <div className="mt-1.5 text-[12.5px] font-medium">{label}</div>
      <div className="text-[11.5px] opacity-70 leading-tight">{hint}</div>
    </button>
  )
}

// split = the clinic / doctor share of a fee — may carry satang (see SPLIT above).
function SumTile({ label, value, color, strong, count, split }) {
  return (
    <div className="min-w-[84px]">
      <div className="flex items-center gap-1.5 text-[12px] text-slate-500">{color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}</div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[18px] font-semibold text-slate-900' : 'text-[15px] font-medium text-slate-700'}`}>{count ? fmtNumber(value) : fmtMoney(value, split ? SPLIT : undefined)}</div>
    </div>
  )
}
// % งานแก้ for one summary row. Always carries its numerator: a rate without its
// denominator ranks a lab that remade its only piece above one that remade 20 of 400.
// `muted` = a สถานะ filter is on, so the rate is a slice, not a verdict — no colour.
function RateTile({ repair, cases, caveat, onSort, sorted }) {
  const rate = repairRate(repair, cases)
  const thin = cases < REPAIR_MIN_N
  const untracked = caveat === 'year'
  const color = untracked || !repair || caveat || thin ? '#64748b'
    : rate >= REPAIR_BAD ? '#e11d48'
      : rate >= REPAIR_WARN ? '#d97706'
        : '#64748b'
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className={sorted ? 'text-rose-600 font-semibold' : ''}>% งานแก้</span>
        {sorted && <ArrowDown size={11} className="text-rose-600" />}
      </div>
      <div className="tabular-nums leading-tight text-[15px] font-medium" style={{ color }}>{untracked ? 'n/a' : cases ? fmtPct(rate) : '—'}</div>
      <div className="text-[11px] text-slate-400 tabular-nums leading-tight">
        {untracked ? `ยังไม่ได้ติ๊กงานแก้ก่อนปี ${REPAIR_FROM_YEAR}` : `${fmtNumber(repair)} / ${fmtNumber(cases)} ชิ้น`}
      </div>
    </>
  )
  const why = untracked ? REPAIR_UNTRACKED
    : thin ? 'งานน้อยเกินไปที่จะตัดสินคุณภาพ'
      : caveat === 'stage' ? 'นับเฉพาะสถานะที่กรอง' : 'สัดส่วนงานแก้ต่องานทั้งหมด'
  if (!onSort) return <div className="min-w-[84px]" title={why}>{body}</div>
  return (
    <button type="button" onClick={onSort}
      title={`${why} · ${sorted ? 'คลิกเพื่อกลับไปเรียงแบบเดิม' : 'คลิกเพื่อเรียงกลุ่มจาก % งานแก้ มาก → น้อย'}`}
      className="min-w-[84px] text-left rounded-md -mx-1 px-1 hover:bg-slate-100/70 transition">
      {body}
    </button>
  )
}
